import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {ChartTooltipJsxProps, ChartTooltipProps} from 'common/components/chart-tooltip';
import {SectionItem} from 'common/components/items-menu/types';
import {SegmentExtras} from 'common/report/insights/charts/insights-bar-chart/types';
import {FlatSortConfig, SortConfig} from 'common/types/reports/bookmark';

export enum BarPattern {
  Striped = `striped`,
}

export type TooltipDataCallback = (
  segment: SegmentWithoutChildren,
  bar: Bar,
  barSegment: BarSegment,
) => ChartTooltipProps;

export interface BarSegment<ExtrasType = any> {
  /**
   * Color for the segment (hex string like #ABC123).
   * Chart will color bars automatically if not specificed (bar 1 => $chart1; bar 2 => #chart2, etc),
   * but individual overrides will be respected (so there may be overlap if you specify an incomplete set).
   * Recommendation is to either specify all or none
   */
  color?: string;

  pattern?: BarPattern;

  value: number;

  /**
   * Label to place below the bar segment in stacked mode, or beside the whole bar in non-stacked mode.
   * Note this holds a stringified numeric value, not the segment name
   */
  label: string;

  /** can store arbitrary data associated with the bar/segment, will be passed through in handlers & callbacks (eg. barClick event, tooltipDataCallback) */
  extras?: ExtrasType;
}

export interface Bar<ExtrasType = any> {
  extras?: ExtrasType;

  /** Label to place next to the bar (i.e. 12.3K) */
  label?: string;

  /** Segments inside of the bar. Stacked bars have multiple segments. Regular bars have just one (in either segments or negativeSegments) */
  segments: Array<BarSegment<ExtrasType>>;

  /** Negative segments inside of the bar. Stacked bars have multiple segments. Regular bars have just one (in either segments or negativeSegments) */
  negativeSegments: Array<BarSegment<ExtrasType>>;

  /** Label to place next to the negative segments for stacked bars */
  negativeLabel?: string;
}

interface SegmentBase<ExtrasType> {
  segmentName: string;
  label: string;
  extras?: ExtrasType;

  // formatted label for the value of child segments - prefer this to be the unsegmented sum
  valueLabel?: string;
}

export interface SegmentWithChildren<ExtrasType = any> extends SegmentBase<ExtrasType> {
  children: Array<Segment<ExtrasType>>;
}

export interface SegmentWithoutChildren<ExtrasType = any> extends SegmentBase<ExtrasType> {
  bars: Array<Bar<ExtrasType>>;
}

// Segment has either bars OR children, not both
export type Segment<ExtrasType = any> = SegmentWithChildren<ExtrasType> | SegmentWithoutChildren<ExtrasType>;

export enum BarValueType {
  Absolute = `absolute`,
  Log = `log`,
}

export interface BarClickEvent<ExtrasType = any> {
  pageX: number;
  pageY: number;
  segment: Segment<ExtrasType>;
  bar: Bar<ExtrasType>;
  barSegment: BarSegment<ExtrasType>;
}

export interface BarVizProps {
  barValueType?: BarValueType;
  /**
   * Object with columnIndex (integer) keys, resizedWidth (float) values as percentages.
   * */
  columnWidths?: ColumnWidths;
  chartData: Segment[];
  downloadMode?: boolean;
  enableBarClickEvents?: boolean;
  hideTooltip?: boolean;
  onBarClick?: (ev: BarClickEvent) => void;
  onColumnWidthChange?: (columnWidths: ColumnWidths) => void;
  onSortingChange?: (sortConfig: SortConfig) => void;
  progressivePage?: number;
  segmentFilterController: SegmentFilterController;
  selectedBarSegment: BarSegment<SegmentExtras>;
  /**
   * Whether to show labels below the bar.
   */
  showSegmentLabels?: boolean;
  isStacked?: boolean;
  sortConfig?: SortConfig;
  tooltipDataCallback: TooltipDataCallback;
}

export interface ColumnWidths {
  [columnIdx: number]: number;
}

export interface BarVizState {
  hasNegativeValues: boolean;
  chartMin: number;
  chartMax: number;
  hoverTooltip: ChartTooltipJsxProps | null;
  columnWidths: ColumnWidths;

  resizeColumn: Element | null;
  resizeColumnIdx: number | null;
  resizeColumnWidth: number;

  selfWidth: number;
  selfHeight: number;

  progressivePage: number;
}

export type OldBarVizProps = {
  chartData: Segment[];
  tooltipDataCallback?: TooltipDataCallback;
};

export interface BarVizJsxProps {
  props: BarVizProps;
  // props: BarVizProps;
}

export interface Header {
  label: string;
  sortOptionItems: Array<SectionItem<FlatSortConfig>>;
}
